import React, { useState } from 'react';
import theme from '../../../theme/globalTheme';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';
import heic2any from 'heic2any';
import {
    Upload,
    Button,
    Card,
    Progress,
    Alert,
    Space,
    Typography,
    List,
    Descriptions,
    Tag,
    Divider,
    Tooltip,
    Spin
} from 'antd';
import {
    UploadOutlined,
    DeleteOutlined,
    ScanOutlined,
    FileOutlined,
    ExclamationCircleOutlined,
    ClearOutlined,
    ReloadOutlined,
    LoadingOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Dragger } = Upload;

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ERROR_CODES = {
  INVALID_FILE: 'E001',
  FILE_TOO_LARGE: 'E002',
  INVALID_IMAGE_FORMAT: 'E003',
  API_AUTH_ERROR: 'E004',
  API_RATE_LIMIT: 'E005',
  API_SERVER_ERROR: 'E006',
  NETWORK_ERROR: 'E007',
  IMAGE_PROCESSING_ERROR: 'E008',
  UNKNOWN_ERROR: 'E999',
  PDF_PROCESSING_ERROR: 'E009',
  PDF_EXTRACTION_ERROR: 'E010',
  HEIC_CONVERSION_ERROR: 'E011'
};

const ERROR_MESSAGES = {
  [ERROR_CODES.INVALID_FILE]: 'Invalid file selected. Please choose an image file.',
  [ERROR_CODES.FILE_TOO_LARGE]: 'File size exceeds the maximum limit of 10MB.',
  [ERROR_CODES.INVALID_IMAGE_FORMAT]: 'Invalid image format. Supported formats: JPG, PNG, JPEG, HEIC.',
  [ERROR_CODES.API_AUTH_ERROR]: 'API authentication failed. Please check your API key.',
  [ERROR_CODES.API_RATE_LIMIT]: 'API rate limit exceeded. Please try again later.',
  [ERROR_CODES.API_SERVER_ERROR]: 'Server error occurred. Please try again later.',
  [ERROR_CODES.NETWORK_ERROR]: 'Network connection error. Please check your internet connection.',
  [ERROR_CODES.IMAGE_PROCESSING_ERROR]: 'Error processing the image. Please try a different image.',
  [ERROR_CODES.UNKNOWN_ERROR]: 'An unknown error occurred. Please try again.',
  [ERROR_CODES.PDF_PROCESSING_ERROR]: 'Error processing PDF file. Please try again.',
  [ERROR_CODES.PDF_EXTRACTION_ERROR]: 'Error extracting images from PDF. Please try again.',
  [ERROR_CODES.HEIC_CONVERSION_ERROR]: 'Error converting HEIC image. Please try again.'
};

const MISTRAL_API_KEY = '5iZczJmWKKSlyjqCavPBrtuMrdZ38YzG';
const MISTRAL_API_URL = 'https://api.mistral.ai/v1/chat/completions';

function PassportAnalyzer() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [pdfImages, setPdfImages] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [processingFiles, setProcessingFiles] = useState([]);
  const [conversionProgress, setConversionProgress] = useState({});

  const isHeicFile = (file) => {
    return file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic');
  };

  const convertHeicToJpeg = async (file) => {
    try {
      setConversionProgress(prev => ({ ...prev, [file.name]: 0 }));
      
      const blob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.8,
        onProgress: (progress) => {
          setConversionProgress(prev => ({ 
            ...prev, 
            [file.name]: Math.round(progress * 100) 
          }));
        }
      });
      
      const convertedFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), {
        type: 'image/jpeg'
      });
      
      setConversionProgress(prev => ({ ...prev, [file.name]: 100 }));
      return convertedFile;
    } catch (error) {
      console.error('HEIC conversion error:', error);
      throw { code: ERROR_CODES.HEIC_CONVERSION_ERROR };
    } finally {
      setConversionProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[file.name];
        return newProgress;
      });
    }
  };

  const processFiles = async (fileList) => {
    const heicFiles = fileList.filter(file => isHeicFile(file.originFileObj));
    const nonHeicFiles = fileList.filter(file => !isHeicFile(file.originFileObj));
    
    let processedFiles = [];

    // Add non-HEIC files directly
    processedFiles = [...nonHeicFiles.map(file => file.originFileObj)];

    // Process HEIC files sequentially
    if (heicFiles.length > 0) {
      for (const file of heicFiles) {
        try {
          setProcessingFiles(prev => [...prev, file.name]);
          const convertedFile = await convertHeicToJpeg(file.originFileObj);
          processedFiles.push(convertedFile);
        } catch (err) {
          setError({
            code: ERROR_CODES.HEIC_CONVERSION_ERROR,
            message: `${file.name}: ${ERROR_MESSAGES[ERROR_CODES.HEIC_CONVERSION_ERROR]}`
          });
        } finally {
          setProcessingFiles(prev => prev.filter(name => name !== file.name));
        }
      }
    }

    return processedFiles;
  };

  const handleFileChange = async (info) => {
    const { fileList } = info;
    
    // Ne traiter que les nouveaux fichiers
    const newFileList = fileList.filter(file => {
      // Vérifier si le fichier n'existe pas déjà dans files
      return !files.some(existingFile => 
        existingFile.name === file.originFileObj.name
      );
    });

    if (newFileList.length === 0) return;

    try {
      // Traiter les nouveaux fichiers
      const processedFiles = await processFiles(newFileList);
      
      // Valider les fichiers
      const validFiles = processedFiles.filter(file => {
        try {
          validateFile(file);
          return true;
        } catch (err) {
          setError({
            code: err.code,
            message: `${file.name}: ${ERROR_MESSAGES[err.code]}`
          });
          return false;
        }
      });

      // Ajouter uniquement les nouveaux fichiers valides
      setFiles(prevFiles => {
        const uniqueFiles = [...prevFiles];
        validFiles.forEach(newFile => {
          if (!uniqueFiles.some(existing => existing.name === newFile.name)) {
            uniqueFiles.push(newFile);
          }
        });
        return uniqueFiles;
      });
      
      if (validFiles.length > 0) {
        setError(null);
      }
    } catch (err) {
      setError({
        code: ERROR_CODES.UNKNOWN_ERROR,
        message: ERROR_MESSAGES[ERROR_CODES.UNKNOWN_ERROR]
      });
    }
  };

  const resetPassports = () => {
    setResetting(true);
    setFiles([]); // Réinitialiser complètement les fichiers
    setResults([]);
    setError(null);
    setProgress(0);
    setCurrentFileIndex(0);
    setPdfImages([]);
    setNumPages(null);
    setProcessingFiles([]);
    setConversionProgress({});
    setResetting(false);
  };

  const removeFile = (fileToRemove) => {
    setFiles(prevFiles => 
      prevFiles.filter(file => file.name !== fileToRemove.name)
    );
  };

  const validateFile = (file) => {
    if (!file) throw { code: ERROR_CODES.INVALID_FILE };
    if (file.size > 10 * 1024 * 1024) throw { code: ERROR_CODES.FILE_TOO_LARGE };
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/heic'];
    const validExtensions = /\.(jpg|jpeg|png|pdf|heic)$/i;
    
    if (!validTypes.includes(file.type) && !validExtensions.test(file.name)) {
      throw { code: ERROR_CODES.INVALID_IMAGE_FORMAT };
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        } catch (error) {
          reject({ code: ERROR_CODES.IMAGE_PROCESSING_ERROR });
        }
      };
      reader.onerror = () => reject({ code: ERROR_CODES.IMAGE_PROCESSING_ERROR });
      reader.readAsDataURL(file);
    });
  };

  const convertImageDataToBase64 = (imageData) => {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const base64String = canvas.toDataURL('image/jpeg').split(',')[1];
          resolve(base64String);
        };
        
        img.onerror = () => {
          reject({ code: ERROR_CODES.IMAGE_PROCESSING_ERROR });
        };
        
        img.src = imageData;
      } catch (error) {
        reject({ code: ERROR_CODES.IMAGE_PROCESSING_ERROR });
      }
    });
  };

  const extractImagesFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const images = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.0 });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;

        const imageData = canvas.toDataURL('image/jpeg');
        images.push({
          data: imageData.split(',')[1],
          pageNum: pageNum
        });
      }

      return images;
    } catch (error) {
      throw { code: ERROR_CODES.PDF_EXTRACTION_ERROR };
    }
  };

  const parseResponse = (response) => {
    const lines = response.split('\n');
    const parsed = {};
    lines.forEach(line => {
      const [key, value] = line.split(':').map(str => str.trim());
      if (key && value) {
        parsed[key] = value;
      }
    });
    return parsed;
  };

  const processFile = async (file) => {
    try {
      let imagesToProcess = [];

      if (file.type === 'application/pdf') {
        imagesToProcess = await extractImagesFromPDF(file);
      } else {
        const base64Image = await convertToBase64(file);
        imagesToProcess = [{ data: base64Image, pageNum: 1 }];
      }

      const results = [];

      for (const image of imagesToProcess) {
        const requestBody = {
          model: "pixtral-12b-2409",
          messages: [
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: "Please analyze this passport image and return ONLY the following information in this exact format (include the labels):" +
                        "Surname: [surname]\n" +
                        "First Name: [first name]\n" +
                        "Birth Date: [date]\n" +
                        "Birth Place: [city or country of birth]\n" +
                        "Passport Number: [number]\n" +
                        "Nationality: [nationality]"
                },
                {
                  type: "image_url",
                  image_url: `data:image/jpeg;base64,${image.data}`
                }
              ]
            }
          ]
        };

        const response = await axios.post(MISTRAL_API_URL, requestBody, {
          headers: {
            'Authorization': `Bearer ${MISTRAL_API_KEY}`,
            'Content-Type': 'application/json'
          }
        });

        const extractedInfo = response.data.choices[0].message.content;
        const parsed = parseResponse(extractedInfo);

        results.push({
          pageNum: image.pageNum,
          data: parsed
        });
      }

      return {
        fileName: file.name,
        data: results,
        error: null
      };
    } catch (err) {
      return {
        fileName: file.name,
        data: null,
        error: err.code ? ERROR_MESSAGES[err.code] : 'Failed to process this file'
      };
    }
  };

  const analyzePassports = async () => {
    if (files.length === 0) {
      setError({
        code: ERROR_CODES.INVALID_FILE,
        message: 'Please select at least one file to analyze'
      });
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setCurrentFileIndex(0);
    setProgress(0);

    const newResults = [];
    
    for (let i = 0; i < files.length; i++) {
      setCurrentFileIndex(i);
      setProgress((i / files.length) * 100);
      
      try {
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        const result = await processFile(files[i]);
        newResults.push(result);
        setResults([...newResults]);
      } catch (err) {
        newResults.push({
          fileName: files[i].name,
          data: null,
          error: 'Failed to process this file'
        });
        setResults([...newResults]);
      }
    }

    setProgress(100);
    setLoading(false);
    setFiles([]);
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: '.jpg,.jpeg,.png,.pdf,.heic',
    fileList: files.map((file, index) => ({
      uid: `${index}-${file.name}`, // Unique ID pour chaque fichier
      name: file.name,
      status: processingFiles.includes(file.name) ? 'uploading' : 'done',
      percent: conversionProgress[file.name],
      originFileObj: file,
    })),
    onChange: handleFileChange,
    onRemove: (file) => {
      removeFile(file);
      return true;
    },
    beforeUpload: () => false,
    progress: {
      strokeColor: {
        '0%': '#108ee9',
        '100%': '#87d068',
      },
      strokeWidth: 3,
      format: percent => percent && `${parseFloat(percent.toFixed(2))}%`,
    },
    showUploadList: {
      showProgress: true,
    },
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={2}>Décoder des passeports</Title>
            <Tooltip title="Reset all data">
              <Button 
                icon={<ReloadOutlined />} 
                onClick={resetPassports}
                disabled={loading || processingFiles.length > 0 || (files.length === 0 && results.length === 0)}
              >
                Réinitialiser
              </Button>
            </Tooltip>
          </div>

          {error && (
            <Alert
              message={`Error Code: ${error.code}`}
              description={error.message}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Dragger {...uploadProps} disabled={loading}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Cliquez ou déposez des fichiers
              </p>
              <p className="ant-upload-hint">
                Formats supportés: JPG, JPEG, PNG, PDF, et HEIC
              </p>
              {processingFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                  <p style={{ marginTop: '8px' }}>
                    Conversion des fichiers HEIC en cours...
                  </p>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {processingFiles.map(fileName => (
                      <div key={fileName}>
                        {fileName} - {conversionProgress[fileName] ? `${conversionProgress[fileName]}%` : 'En cours...'}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Dragger>

            <Button
              type="primary"
              icon={<ScanOutlined />}
              onClick={analyzePassports}
              disabled={files.length === 0 || loading || processingFiles.length > 0}
              loading={loading}
              block
            >
              {loading ? 'Traitement en cours...' : 
               processingFiles.length > 0 ? `Conversion HEIC (${processingFiles.length} fichier${processingFiles.length > 1 ? 's' : ''})...` : 
               'Analyser les passeports'}
            </Button>
          </Space>

          {loading && (
            <Card>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>
                  Traitement du fichier {currentFileIndex + 1} sur {files.length}
                </Text>
                <Progress 
                  percent={Math.round(progress)} 
                  status="active"
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </Space>
            </Card>
          )}

          {results.length > 0 && (
            <List
              dataSource={results}
              renderItem={(result, index) => (
                <List.Item>
                  <Card 
                    title={
                      <Space>
                        <Tag color="blue">Passeport #{index + 1}</Tag>
                        <FileOutlined />
                        {result.fileName}
                      </Space>
                    }
                    style={{ width: '100%' }}
                    extra={
                      <Text type="secondary">
                        {index + 1} sur {results.length}
                      </Text>
                    }
                  >
                    {result.error ? (
                      <Alert message={result.error} type="error" showIcon />
                    ) : (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {result.data.map((pageResult, pageIndex) => (
                          <Card
                            key={pageIndex}
                            type="inner"
                            title={
                              <Space>
                                <Tag color="green">Page {pageResult.pageNum}</Tag>
                                {result.data.length > 1 && 
                                  <Text type="secondary">
                                    ({pageIndex + 1} sur {result.data.length})
                                  </Text>
                                }
                              </Space>
                            }
                          >
                            <Descriptions
                              bordered
                              column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                              title={
                                <Space size="middle">
                                  <Tag color="blue">Passeport #{index + 1}</Tag>
                                  <Text type="secondary">Fichier: {result.fileName}</Text>
                                </Space>
                              }
                            >
                              {Object.entries(pageResult.data).map(([key, value]) => (
                                <Descriptions.Item 
                                  key={key} 
                                  label={<Text strong>{key}</Text>}
                                >
                                  {value || 'Non trouvé'}
                                </Descriptions.Item>
                              ))}
                            </Descriptions>
                          </Card>
                        ))}
                      </Space>
                    )}
                  </Card>
                </List.Item>
              )}
            />
          )}
        </Space>
      </Card>
    </div>
  );
}

export default PassportAnalyzer;